'use strict';

function upDecorateChampCheckoutUrl(url) {
  if (
    url &&
    window.UltimaPeakTracking &&
    typeof window.UltimaPeakTracking.decorateChampUrl === 'function'
  ) {
    return window.UltimaPeakTracking.decorateChampUrl(url);
  }

  return url;
}


/**
 * Main Product V3 Section JavaScript
 * Custom Elements for gallery, plan CTA, accordion, and selling plans
 */

/* ==========================================================================
   Gallery Component
   ========================================================================== */
if (!customElements.get('mpv1-gallery')) {
  class Mpv1Gallery extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.thumbnails = [];
      this.touchStartX = 0;
      this.touchEndX = 0;
    }

    connectedCallback() {
      this.mainImage = this.querySelector('.mpv1-gallery__main-img');
      this.thumbnails = Array.from(this.querySelectorAll('.mpv1-gallery__thumb'));
      this.prevBtn = this.querySelector('[data-gallery-prev]');
      this.nextBtn = this.querySelector('[data-gallery-next]');
      this.mainWrapper = this.querySelector('.mpv1-gallery__main');
      this.thumbsContainer = this.querySelector('.mpv1-gallery__thumbs');

      if (!this.mainImage || this.thumbnails.length === 0) return;

      // Find initial active thumbnail
      this.currentIndex = this.thumbnails.findIndex(thumb => thumb.classList.contains('is-active'));
      if (this.currentIndex === -1) this.currentIndex = 0;

      this.bindEvents();
      this.updateNavButtons();
    }

    bindEvents() {
      // Thumbnail clicks
      this.thumbnails.forEach((thumb, index) => {
        thumb.addEventListener('click', () => this.goToSlide(index));
      });

      // Navigation buttons
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.prev());
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.next());
      }

      // Keyboard navigation
      this.addEventListener('keydown', (e) => this.handleKeydown(e));

      // Touch/swipe support on main image wrapper
      if (this.mainWrapper) {
        this.mainWrapper.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
        this.mainWrapper.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: true });
      }
    }

    handleKeydown(e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.prev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.next();
      }
    }

    handleTouchStart(e) {
      this.touchStartX = e.changedTouches[0].screenX;
    }

    handleTouchEnd(e) {
      this.touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    }

    handleSwipe() {
      const swipeThreshold = 50;
      const diff = this.touchStartX - this.touchEndX;

      if (Math.abs(diff) < swipeThreshold) return;

      if (diff > 0) {
        // Swipe left - go next
        this.next();
      } else {
        // Swipe right - go prev
        this.prev();
      }
    }

    prev() {
      if (this.currentIndex > 0) {
        this.goToSlide(this.currentIndex - 1);
      }
    }

    next() {
      if (this.currentIndex < this.thumbnails.length - 1) {
        this.goToSlide(this.currentIndex + 1);
      }
    }

    goToSlide(index) {
      if (index < 0 || index >= this.thumbnails.length) return;

      const thumb = this.thumbnails[index];
      const fullSrc = thumb.dataset.full;
      const srcset = thumb.dataset.srcset;
      const alt = thumb.dataset.alt || '';

      if (!fullSrc) return;

      // Update main image — set srcset before src to avoid a double network
      // request: if src fires first the browser starts fetching the 1200px
      // image, then cancels/restarts when srcset is evaluated a tick later.
      this.mainImage.classList.add('is-loading');
      const onLoad = () => {
        this.mainImage.classList.remove('is-loading');
        this.mainImage.removeEventListener('load', onLoad);
      };
      this.mainImage.addEventListener('load', onLoad);

      if (srcset) {
        this.mainImage.srcset = srcset;
      }
      this.mainImage.src = fullSrc;
      this.mainImage.alt = alt;

      // Update active states
      this.thumbnails.forEach((t, i) => {
        t.classList.toggle('is-active', i === index);
        t.setAttribute('aria-current', i === index ? 'true' : 'false');
      });

      this.currentIndex = index;
      this.updateNavButtons();

      // Scroll active thumbnail into view — scroll the strip container directly
      // to avoid scrollIntoView bubbling up to the viewport and shifting the layout.
      if (this.thumbsContainer) {
        const scrollLeft = thumb.offsetLeft - (this.thumbsContainer.offsetWidth / 2) + (thumb.offsetWidth / 2);
        this.thumbsContainer.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }
    }

    updateNavButtons() {
      if (this.prevBtn) {
        this.prevBtn.disabled = this.currentIndex === 0;
        this.prevBtn.setAttribute('aria-disabled', this.currentIndex === 0 ? 'true' : 'false');
      }
      if (this.nextBtn) {
        this.nextBtn.disabled = this.currentIndex === this.thumbnails.length - 1;
        this.nextBtn.setAttribute('aria-disabled', this.currentIndex === this.thumbnails.length - 1 ? 'true' : 'false');
      }
    }
  }

  customElements.define('mpv1-gallery', Mpv1Gallery);
}

/* ==========================================================================
   Pack Selector Component
   Quantity-based bundle picker — single variant, no selling plans.
   Each card has its own ORDER NOW button. Clicking it directly adds that
   plan's quantity to cart and redirects to checkout.
   Passes supply_plan + rebill_days as line item properties so the
   external subscription platform (Phoenix / Checkout Champ) can read
   them at checkout.
   ========================================================================== */
if (!customElements.get('mpv3-pack-selector')) {
  class Mpv3PackSelector extends HTMLElement {
    constructor() {
      super();
      this.loadingBtn = null;
      this.errorEl = null;
      this.layoutType = null;
    }

    connectedCallback() {
      this.variantId = this.dataset.variantId;
      this.layoutType = this.dataset.layout || 'cards';
      this.errorEl = this.querySelector('.mpv3-pack__error');

      if (!this.variantId) return;

      if (this.layoutType === 'switchers') {
        this._initSwitchers();
      } else {
        this._initCards();
      }
    }

    _initCards() {
      this.cards = Array.from(this.querySelectorAll('.mpv3-pack'));
      if (this.cards.length === 0) return;

      // Each card has its own CTA button — bind per-card
      this.cards.forEach(card => {
        const btn = card.querySelector('.mpv3-pack__cta-btn');
        if (btn) {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            this._handleAddToCartFromCard(card, btn);
          });
        }
      });
    }

    _initSwitchers() {
      this.radios = Array.from(this.querySelectorAll('.mpv3-switcher__radio'));
      this.switcherBtn = this.querySelector('.mpv3-switchers__cta-btn');
      this.switcherBtnText = this.querySelector('.mpv3-switchers__cta-text');
      this.baseButtonText = this.switcherBtnText ? this.switcherBtnText.textContent.trim() : 'ADD TO CART';

      if (this.radios.length === 0 || !this.switcherBtn) return;

      // Select default option
      const defaultOption = this.querySelector('[data-default] .mpv3-switcher__radio');
      if (defaultOption) {
        defaultOption.checked = true;
      } else if (this.radios[0]) {
        this.radios[0].checked = true;
      }

      // Sync price row after BucksCC converts .money spans (loads async, so rAF alone is unreliable)
      this._syncAfterCurrencyConversion();

      // Update selection state on radio change
      this.radios.forEach(radio => {
        radio.addEventListener('change', () => {
          this._updateSwitcherSelection();
        });
      });
    }

    _syncAfterCurrencyConversion() {
      const selectedRadio = this.querySelector('.mpv3-switcher__radio:checked');
      const label = selectedRadio?.closest('label');
      const totalPriceEl = label?.querySelector('.mpv3-switcher__total-amount');

      if (!totalPriceEl) {
        this._updateSwitcherSelection();
        return;
      }

      // If BucksCC already ran (text differs from the raw USD data attribute), update immediately
      const usdText = label.dataset.totalPrice || '';
      const currentText = totalPriceEl.textContent.trim();
      if (currentText && currentText !== usdText) {
        this._updateSwitcherSelection();
        return;
      }

      // Otherwise observe the span for BucksCC's mutation, then update
      let settled = false;
      const run = () => {
        if (settled) return;
        settled = true;
        observer.disconnect();
        this._updateSwitcherSelection();
      };

      const observer = new MutationObserver(run);
      observer.observe(totalPriceEl, { childList: true, subtree: true, characterData: true });
      setTimeout(run, 1500); // fallback: BucksCC absent or conversion not needed
    }

    _updateSwitcherSelection() {
      // Find selected radio and get the total price from parent label
      const selectedRadio = this.querySelector('.mpv3-switcher__radio:checked');
      if (!selectedRadio) return;

      const label = selectedRadio.closest('label');
      const totalPriceEl = label?.querySelector('.mpv3-switcher__total-amount');
      const totalPrice = totalPriceEl?.textContent?.trim() || label?.dataset.totalPrice || '';
      const comparePriceEl = label?.querySelector('.mpv3-switcher__compare');
      const comparePrice = comparePriceEl?.textContent?.trim() || label?.dataset.compareTotalPrice || '';
      const checkoutUrl = label ? label.dataset.checkoutUrl : '';

      // Update button text to show: "ADD TO CART - $149.70"
      if (this.switcherBtnText) {
        if (totalPrice) {
          this.switcherBtnText.innerHTML = `${this.baseButtonText} - ${totalPrice} ${
  comparePrice ? `<span class="compare-price">${comparePrice}</span>` : ''
}`;
        } else {
          this.switcherBtnText.textContent = this.baseButtonText;
        }
      }

      // Update Standalone Price Rows if they exist
      const currentEls = document.querySelectorAll('.js-mpv3-price-current');
      const oldEls = document.querySelectorAll('.js-mpv3-price-old');
      const saleEls = document.querySelectorAll('.js-mpv3-price-sale');

      currentEls.forEach(el => { el.textContent = totalPrice; });
      oldEls.forEach(el => {
        el.textContent = comparePrice;
        el.style.display = comparePrice ? 'inline' : 'none';
      });

      saleEls.forEach(el => {
        const template = el.dataset.saleTemplate;
        if (!template) return;

        // Restore the safe placeholders back to readable form for matching
        let saleText = template;
        const subtitle = label ? (label.dataset.subtitle || '') : '';
        const badgeText = label ? (label.dataset.badge || '') : '';

        // 1a. {{ sale_discount }} — BucksCC-converted fixed amount from hidden span
        const saleDiscountEl = el.parentElement?.querySelector('.mpv3-price-row__sale-discount');
        if (saleDiscountEl) {
          const saleDiscountText = saleDiscountEl.textContent?.trim() || '';
          if (saleDiscountText) {
            saleText = saleText.replace(/__OPEN__\s*sale_discount\s*__CLOSE__/g, saleDiscountText);
          }
        }

        // 1b. {{ discount_amount }} — auto-calculated savings from pack selector prices
        const rawCurrentPrice = totalPrice || '';
        const rawComparePrice = comparePrice || '';
        if (rawCurrentPrice && rawComparePrice) {
            // Extract numeric substring (first digit to last) to avoid currency symbols
            // like 'Bs.' whose dot would corrupt number parsing.
            const extractNumStr = s => (s.match(/\d[\d.,]*\d|\d/) || [''])[0];
            const parseMoney = s => {
              const n = extractNumStr(s);
              if (!n) return NaN;
              const dots = (n.match(/\./g) || []).length;
              const commas = (n.match(/,/g) || []).length;
              // Multiple dots = European format: dot=thousands, comma=decimal (e.g. 1.989.687,99)
              if (dots > 1) return parseFloat(n.replace(/\./g, '').replace(',', '.'));
              // Multiple commas = comma-thousands (e.g. 1,234,567.89)
              if (commas > 1) return parseFloat(n.replace(/,/g, ''));
              // Both present: whichever comes last is the decimal separator
              if (dots === 1 && commas === 1)
                return n.lastIndexOf(',') > n.lastIndexOf('.')
                  ? parseFloat(n.replace('.', '').replace(',', '.'))
                  : parseFloat(n.replace(',', ''));
              return parseFloat(n.replace(',', '.'));
            };
            const currentNum = parseMoney(rawCurrentPrice);
            const compareNum = parseMoney(rawComparePrice);
            if (!isNaN(currentNum) && !isNaN(compareNum) && compareNum > currentNum) {
                const discount = compareNum - currentNum;
                // Format discount using same thousands/decimal style as the source currency
                const srcNumStr = extractNumStr(rawCurrentPrice);
                const isEuropean = (srcNumStr.match(/\./g) || []).length > 1 ||
                  (srcNumStr.includes(',') && srcNumStr.lastIndexOf(',') > srcNumStr.lastIndexOf('.'));
                const discountFormatted = isEuropean
                  ? new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(discount)
                  : discount.toFixed(2).replace(/\.00$/, '');
                // Extract currency symbol: everything before the first digit
                const currencyPrefix = rawCurrentPrice.match(/^[^\d]+/)?.[0]?.trim() || '';
                const currencySuffix = !currencyPrefix
                  ? (rawCurrentPrice.match(/[^\d,.]+$/)?.[0]?.trim() || '')
                  : '';
                let discountStr = discountFormatted;
                if (currencyPrefix) discountStr = currencyPrefix + ' ' + discountStr;
                else if (currencySuffix) discountStr = discountStr + ' ' + currencySuffix;
                saleText = saleText.replace(/__OPEN__\s*discount_amount\s*__CLOSE__/g, discountStr);
            }
        }

        // 2. Plan subtitle (e.g. "You save 50%")
        saleText = saleText.replace(/__OPEN__\s*discount_subtitle\s*__CLOSE__/g, subtitle);

        // 3. Plan badge (e.g. "Best Value")
        saleText = saleText.replace(/__OPEN__\s*plan_badge\s*__CLOSE__/g, badgeText);

        // Show badge only if all placeholders were replaced
        const hasUnfilled = saleText.includes('__OPEN__');
        if (hasUnfilled || saleText.trim() === '') {
            el.style.display = 'none';
        } else {
            el.textContent = saleText;
            el.style.display = 'inline-block';
        }
      });

      // Update anchor href to checkout URL from selected option
      if (this.switcherBtn && checkoutUrl) {
        this.switcherBtn.href = upDecorateChampCheckoutUrl(checkoutUrl);
      }
    }

    async _handleAddToCartFromCard(card, btn) {
      if (btn.classList.contains('is-loading')) return;

      this._clearError();
      this._setLoading(btn, true);

      const qty        = parseInt(card.dataset.qty, 10) || 1;
      const supplyPlan = card.dataset.supplyPlan || '';
      const rebillDays = card.dataset.rebillDays || '';

      await this._addToCart(qty, supplyPlan, rebillDays, btn);
    }

    async _handleAddToCartFromSwitcher(btn) {
      if (btn.classList.contains('is-loading')) return;

      // Find selected radio
      const selectedRadio = this.querySelector('.mpv3-switcher__radio:checked');
      if (!selectedRadio) {
        this._showError('Please select a supply option');
        return;
      }

      this._clearError();
      this._setLoading(btn, true);

      // Get data from parent label
      const label = selectedRadio.closest('label');
      const qty        = parseInt(label.dataset.qty, 10) || 1;
      const supplyPlan = label.dataset.supplyPlan || '';
      const rebillDays = label.dataset.rebillDays || '';

      await this._addToCart(qty, supplyPlan, rebillDays, btn);
    }

    async _addToCart(qty, supplyPlan, rebillDays, btn) {
      const cartItem = {
        id: parseInt(this.variantId, 10),
        quantity: qty,
        properties: {}
      };

      // Only attach properties that have values so the cart stays clean
      if (supplyPlan) cartItem.properties['_supply_plan'] = supplyPlan;
      if (rebillDays) cartItem.properties['_rebill_days'] = rebillDays;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items: [cartItem] })
        });

        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.description || 'Could not add to cart');
        }

        const data = await response.json();

        // Notify GTM / GA4 listeners
        document.dispatchEvent(new CustomEvent('cart:item:added', {
          bubbles: true,
          detail: {
            items: data.items || data,
            variantId: cartItem.id,
            quantity: qty,
            supplyPlan,
            rebillDays
          }
        }));

        this._setLoading(btn, false);

       window.updateCartDrawer();
      } catch (err) {
        console.error('[mpv3-pack-selector] Add to cart error:', err);
        this._setLoading(btn, false);
        this._showError(err.message || 'Something went wrong. Please try again.');
      }
    }

    _setLoading(btn, loading) {
      btn.classList.toggle('is-loading', loading);
      btn.setAttribute('aria-busy', loading ? 'true' : 'false');
      this.loadingBtn = loading ? btn : null;
    }

    _showError(message) {
      if (this.errorEl) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }
    }

    _clearError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }
  }

  customElements.define('mpv3-pack-selector', Mpv3PackSelector);
}

/* ==========================================================================
   Accordion Component
   ========================================================================== */
if (!customElements.get('mpv1-accordion')) {
  class Mpv1Accordion extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
    }

    connectedCallback() {
      this.trigger = this.querySelector('.mpv1-accordion__trigger');
      this.panel = this.querySelector('.mpv1-accordion__panel');
      this.content = this.querySelector('.mpv1-accordion__content');

      if (!this.trigger || !this.panel) return;

      // Set initial ARIA attributes
      const triggerId = `mpv1-accordion-trigger-${this.generateId()}`;
      const panelId = `mpv1-accordion-panel-${this.generateId()}`;

      this.trigger.setAttribute('id', triggerId);
      this.trigger.setAttribute('aria-controls', panelId);
      this.trigger.setAttribute('aria-expanded', 'false');

      this.panel.setAttribute('id', panelId);
      this.panel.setAttribute('aria-labelledby', triggerId);
      this.panel.setAttribute('role', 'region');

      this.trigger.addEventListener('click', () => this.toggle());

      // Support keyboard
      this.trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.toggle();
        }
      });
    }

    generateId() {
      return Math.random().toString(36).substring(2, 9);
    }

    toggle() {
      this.isOpen = !this.isOpen;
      this.classList.toggle('is-open', this.isOpen);
      this.trigger.setAttribute('aria-expanded', this.isOpen ? 'true' : 'false');

      if (this.content) {
        if (this.isOpen) {
          // Measure actual content height before opening so the CSS transition
          // animates to the correct pixel value (P1 fix: stale height on close).
          const height = this.content.scrollHeight;
          this.style.setProperty('--mpv1-accordion-height', `${height}px`);
        } else {
          // Reset the height var on close so a subsequent open re-measures correctly.
          this.style.removeProperty('--mpv1-accordion-height');
        }
      }
    }

    open() {
      if (!this.isOpen) this.toggle();
    }

    close() {
      if (this.isOpen) this.toggle();
    }
  }

  customElements.define('mpv1-accordion', Mpv1Accordion);
}
